import { useSyncExternalStore } from 'react';
import { DEFAULT_SPEC, PRESETS, paletteCss, parseSpec, sameSpec, type ThemeSpec } from './palette';

/**
 * Appearance choices that live on this device: the theme's colours (see
 * `palette.ts`, which works out every token from them) and which day the week
 * starts on. Light / dark / system is in theme.ts. Nothing here is sent to the
 * server.
 *
 * The colours are applied as one <style> element of CSS variables. Its text is
 * also kept in storage, so public/theme-init.js can put it in place before the
 * first paint, with no flash of the default colours.
 */

export type WeekStart = 'monday' | 'sunday';

const PALETTE_KEY = 'deeds.palette';
/** The stylesheet for the saved palette, for theme-init.js. Keep the key in step with it. */
const PALETTE_CSS_KEY = 'deeds.paletteCss';
/** The four accents of the first appearance settings, carried into the presets of the same name. */
const OLD_ACCENT_KEY = 'deeds.accent';
const WEEK_KEY = 'deeds.weekStart';
const STYLE_ID = 'deeds-palette';
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());
const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

let cached: { raw: string | null; spec: ThemeSpec } | null = null;

function readPalette(): ThemeSpec {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(PALETTE_KEY);
    if (raw === null) {
      const old = localStorage.getItem(OLD_ACCENT_KEY);
      const carried = PRESETS.find((p) => p.id === old);
      if (carried) return carried.spec;
    }
  } catch {
    return DEFAULT_SPEC;
  }
  // The same object while storage holds the same text, as useSyncExternalStore needs.
  if (cached && cached.raw === raw) return cached.spec;
  let spec: ThemeSpec = DEFAULT_SPEC;
  try {
    spec = (raw && parseSpec(JSON.parse(raw))) || DEFAULT_SPEC;
  } catch {
    // A damaged value: the default look.
  }
  cached = { raw, spec };
  return spec;
}

function applyPalette(spec: ThemeSpec): string {
  const css = paletteCss(spec);
  let style = document.getElementById(STYLE_ID);
  if (!(style instanceof HTMLStyleElement)) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    document.head.append(style);
  }
  if (style.textContent !== css) style.textContent = css;
  // A short fingerprint, so observers (the desktop title bar) see each change.
  document.documentElement.setAttribute('data-palette', fingerprint(css));
  paintThemeColor();
  return css;
}

/** The browser's own bar on phones (and the installed app's) takes the top bar's colour. */
export function paintThemeColor(): void {
  const chrome = getComputedStyle(document.documentElement).getPropertyValue('--chrome').trim();
  if (!chrome) return;
  for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    meta.content = chrome;
  }
}

function fingerprint(text: string): string {
  let h = 0;
  for (let i = 0; i < text.length; i++) h = (h * 31 + text.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

export function setPalette(spec: ThemeSpec): void {
  const css = applyPalette(spec);
  try {
    if (sameSpec(spec, DEFAULT_SPEC)) localStorage.removeItem(PALETTE_KEY);
    else localStorage.setItem(PALETTE_KEY, JSON.stringify(spec));
    localStorage.setItem(PALETTE_CSS_KEY, css);
    localStorage.removeItem(OLD_ACCENT_KEY);
  } catch {
    // Storage may be unavailable; the colours still apply for this page.
  }
  notify();
}

/** The theme's colours as saved on this device, and a setter that applies and saves them. */
export function usePalette(): [ThemeSpec, (spec: ThemeSpec) => void] {
  const spec = useSyncExternalStore(subscribe, readPalette, () => DEFAULT_SPEC);
  return [spec, setPalette];
}

function readWeekStart(): WeekStart {
  try {
    return localStorage.getItem(WEEK_KEY) === 'sunday' ? 'sunday' : 'monday';
  } catch {
    return 'monday';
  }
}

export function setWeekStart(day: WeekStart): void {
  try {
    if (day === 'monday') localStorage.removeItem(WEEK_KEY);
    else localStorage.setItem(WEEK_KEY, day);
  } catch {
    // ignore
  }
  notify();
}

export function useWeekStart(): [WeekStart, (d: WeekStart) => void] {
  const day = useSyncExternalStore(subscribe, readWeekStart, () => 'monday' as WeekStart);
  return [day, setWeekStart];
}

/** Applies the saved colours (theme-init.js has usually put them in place already). */
export function initAppearance(): void {
  const css = applyPalette(readPalette());
  try {
    localStorage.setItem(PALETTE_CSS_KEY, css);
  } catch {
    // ignore
  }
}

/** True when the person asked the OS for less motion; counters then jump instead of counting. */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );
}
